import { Link } from '@tanstack/react-router'
import { FileText, Scale } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { useTranslation } from 'react-i18next'
import remarkGfm from 'remark-gfm'
import {
  LEGAL_CONTACT_EMAIL,
  LEGAL_DOCUMENT_PATHS,
  LEGAL_OPERATOR,
  getLegalDocument,
} from '../content/legal-documents'
import type { Components } from 'react-markdown'
import type { LegalDocumentKey } from '../content/legal-documents'
import logoBlack from '@/assets/logo-black.svg'
import { GtmHalftoneBackground } from '@/features/gtm/components/gtm-halftone-background'
import { useGtmTheme } from '@/features/gtm/components/use-gtm-theme'
import { useCurrentLang } from '@/shared/i18n/use-current-lang'
import { publicRouteHref } from '@/shared/seo/seo-config'

const DOCUMENT_LABELS: Record<LegalDocumentKey, string> = {
  terms: 'Terms of Service',
  privacy: 'Privacy Policy',
  refund: 'Refund Policy',
}

const ROUTE_KEYS = {
  terms: 'terms',
  privacy: 'privacy',
  refund: 'refund',
} as const

/** Bare paths used for links between the documents ("privacy") → their key. */
const KEY_BY_PATH = new Map(
  (Object.keys(LEGAL_DOCUMENT_PATHS) as Array<LegalDocumentKey>).map((key) => [
    LEGAL_DOCUMENT_PATHS[key],
    key,
  ]),
)

function legalHref(key: LegalDocumentKey, lang: string): string {
  return publicRouteHref(ROUTE_KEYS[key], lang)
}

/** Markdown elements styled like the rest of the public site. */
function markdownComponents(lang: string): Components {
  return {
    p: ({ children }) => (
      <p className="mt-3 max-w-4xl text-sm leading-7 text-muted-foreground first:mt-0">
        {children}
      </p>
    ),
    ul: ({ children }) => <ul className="mt-4 grid gap-2.5">{children}</ul>,
    ol: ({ children }) => (
      <ol className="mt-4 grid list-decimal gap-2.5 pl-5 text-sm leading-6 text-muted-foreground">
        {children}
      </ol>
    ),
    li: ({ children }) => (
      <li className="flex gap-3 text-sm leading-6 text-muted-foreground [ol_&]:list-item">
        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent-foreground [ol_&]:hidden" />
        <span className="min-w-0">{children}</span>
      </li>
    ),
    strong: ({ children }) => (
      <strong className="font-semibold text-foreground">{children}</strong>
    ),
    em: ({ children }) => <em className="italic">{children}</em>,
    a: ({ href = '', children }) => {
      // Links between the documents are written as bare paths ("privacy").
      const linkedKey = KEY_BY_PATH.get(href)
      const external = /^https?:\/\//.test(href) || href.startsWith('mailto:')
      return (
        <a
          href={linkedKey ? legalHref(linkedKey, lang) : href}
          className="font-medium text-foreground underline underline-offset-4"
          {...(external && !href.startsWith('mailto:')
            ? { target: '_blank', rel: 'noopener noreferrer' }
            : {})}
        >
          {children}
        </a>
      )
    },
    table: ({ children }) => (
      <div className="mt-4 overflow-x-auto rounded-md border">
        <table className="w-full min-w-[32rem] text-left text-sm">
          {children}
        </table>
      </div>
    ),
    thead: ({ children }) => (
      <thead className="bg-muted/50 text-foreground">{children}</thead>
    ),
    th: ({ children }) => <th className="px-3 py-2 font-medium">{children}</th>,
    td: ({ children }) => (
      <td className="border-t px-3 py-2 align-top leading-6 text-muted-foreground">
        {children}
      </td>
    ),
  }
}

export function LegalPage({ documentKey }: { documentKey: LegalDocumentKey }) {
  const lang = useCurrentLang()
  const { t } = useTranslation('gtm')
  const document = getLegalDocument(documentKey)
  const components = markdownComponents(lang)
  // The documents belong to the Laplace x GTM site, so they use its theme.
  useGtmTheme()

  return (
    <main className="theme-gtm relative isolate min-h-screen overflow-x-clip bg-background text-foreground">
      <GtmHalftoneBackground quiet />
      <header className="relative z-10 border-b border-sidebar-border bg-background/65 text-foreground backdrop-blur-sm">
        <div className="flex h-14 w-full items-center px-2">
          <Link
            to="/$lang"
            params={{ lang }}
            className="flex min-w-0 items-center gap-1.5"
          >
            <img src={logoBlack} alt="" className="h-7 w-auto sm:h-8" />
            <span className="truncate text-base font-semibold sm:text-lg">
              {t('brand')}
            </span>
          </Link>
        </div>
      </header>

      <div className="relative z-10 border-b bg-card/85">
        <div className="mx-auto grid w-full max-w-7xl gap-8 px-4 py-12 sm:px-6 lg:grid-cols-[1fr_22rem] lg:px-8 lg:py-16">
          <div className="min-w-0 max-w-4xl">
            <div className="inline-flex items-center gap-2 rounded-md border bg-background px-3 py-1 text-xs font-medium text-muted-foreground">
              <Scale className="h-3.5 w-3.5" />
              Legal
            </div>
            <h1 className="mt-6 font-display text-4xl leading-tight font-medium tracking-[-0.015em] text-balance sm:text-5xl">
              {document.title}
            </h1>
            {document.intro ? (
              <div className="mt-5">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={components}
                >
                  {document.intro}
                </ReactMarkdown>
              </div>
            ) : null}
          </div>

          <aside className="h-fit rounded-lg border bg-background p-5">
            <dl className="grid gap-4 text-sm">
              {document.updated ? (
                <div>
                  <dt className="text-muted-foreground">Last updated</dt>
                  <dd className="mt-1 font-medium">{document.updated}</dd>
                </div>
              ) : null}
              <div>
                <dt className="text-muted-foreground">Provided by</dt>
                <dd className="mt-1 font-medium">{LEGAL_OPERATOR}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Contact</dt>
                <dd className="mt-1 font-medium">
                  <a
                    href={`mailto:${LEGAL_CONTACT_EMAIL}`}
                    className="underline-offset-4 hover:underline"
                  >
                    {LEGAL_CONTACT_EMAIL}
                  </a>
                </dd>
              </div>
            </dl>
            <nav className="mt-5 grid gap-1 border-t pt-5">
              {(Object.keys(DOCUMENT_LABELS) as Array<LegalDocumentKey>).map(
                (key) =>
                  key === documentKey ? (
                    <span
                      key={key}
                      className="rounded-md bg-accent px-3 py-2 text-sm font-medium text-accent-foreground"
                    >
                      {DOCUMENT_LABELS[key]}
                    </span>
                  ) : (
                    <a
                      key={key}
                      href={legalHref(key, lang)}
                      className="rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                    >
                      {DOCUMENT_LABELS[key]}
                    </a>
                  ),
              )}
            </nav>
          </aside>
        </div>
      </div>

      <div className="relative z-10 mx-auto grid w-full max-w-7xl gap-8 px-4 py-8 sm:px-6 lg:grid-cols-[16rem_1fr] lg:px-8">
        <aside className="hidden lg:block">
          <div className="sticky top-24 max-h-[calc(100vh-8rem)] overflow-y-auto rounded-lg border bg-card/85 p-4">
            <p className="mb-3 text-sm font-semibold">Sections</p>
            <nav className="grid gap-1">
              {document.sections.map((section) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className="rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                >
                  {section.title}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        <div className="grid min-w-0 gap-6">
          {document.sections.map((section) => (
            <section
              key={section.id}
              id={section.id}
              className="min-w-0 scroll-mt-24 rounded-lg border bg-card/85 px-5 py-6 sm:px-6"
            >
              <div className="mb-4 flex items-start gap-3">
                <div className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent text-accent-foreground">
                  <FileText className="h-4 w-4" />
                </div>
                <h2 className="text-2xl leading-tight font-semibold text-balance">
                  {section.title}
                </h2>
              </div>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={components}
              >
                {section.markdown}
              </ReactMarkdown>
            </section>
          ))}
        </div>
      </div>
    </main>
  )
}
